import {Injectable} from '@angular/core';
import {Align, Chart, Color, Point, ScriptableScaleContext, Tick} from 'chart.js';
import {Utils} from '@astek/utils';
import {PressureGraphConfig} from '../../../../core/services/config/pressure-graph-config';
import {ThemeService} from '../../../../core/services/theme/theme.service';
import {Measurement} from '../data/measurement';
import {PatientData} from '../data/patient-data';

@Injectable()
export class GraphService {

  private _patientData!: PatientData;

  constructor(
    private readonly _themeService: ThemeService,
  ) {
  }

  public get darkTheme(): boolean {
    return this._themeService.darkTheme;
  }

  public get firstMeasurement(): Measurement | null {
    return this._patientData.firstMeasurement;
  }

  public get lastMeasurement(): Measurement | null {
    return this._patientData.lastMeasurement;
  }

  public init(patientData: PatientData): void {
    this._patientData = patientData;
  }

  public timestampToSeconds(timestamp: number): number {
    return (timestamp - (this.firstMeasurement?.timestamp ?? 0)) / 1000;
  }

  public secondsToTimestamp(seconds: number): number {
    return (1000 * seconds) + (this.firstMeasurement?.timestamp ?? 0);
  }

  public setupAxisScale(
    chart: Chart<'line', Array<Point>>,
    graphConfig: PressureGraphConfig,
    axis: string,
    position: 'left' | 'top' | 'right' | 'bottom' | 'center',
    min: number,
    max: number,
    minRange: number,
    stepSize: number,
    ticksAlignment: Align | 'inner',
    ticksCallback: ((tickValue: number | string, index: number, ticks: Array<Tick>) => string | null) | null = null,
    ticksColorCallback: ((context: ScriptableScaleContext) => Color | undefined) | null = null,
    gridColorCallback: ((context: ScriptableScaleContext) => Color | undefined) | null = null,
  ): void {
    if (Utils.isNullOrUndefined(chart.options.scales)) {
      chart.options.scales = {};
    }
    chart.options.scales[axis] = {
      type: 'linear',
      position: position,
      min: min,
      max: max,
      border: {
        width: 0,
      },
      grid: {
        drawOnChartArea: true,
        drawTicks: false,
        lineWidth: graphConfig.graduations.lines.width,
        color: graphConfig.graduations.lines.color.fromTheme(this.darkTheme),
      },
      ticks: {
        stepSize: stepSize,
        includeBounds: false,
        autoSkip: false,
        minRotation: 0,
        maxRotation: 0,
        labelOffset: 0,
        padding: 4,
        showLabelBackdrop: false,
        align: ticksAlignment,
        color: graphConfig.graduations.color.fromTheme(this.darkTheme),
        font: {
          family: 'DINNextLTPro',
          size: graphConfig.graduations.font.size,
          style: graphConfig.graduations.font.style,
          weight: graphConfig.graduations.font.weight,
          lineHeight: graphConfig.graduations.font.lineHeight,
        },
      },
    };
    if (Utils.isNotNullOrUndefined(ticksCallback)) {
      chart.options.scales[axis]!.ticks!.callback = ticksCallback;
    }
    if (Utils.isNotNullOrUndefined(ticksColorCallback)) {
      chart.options.scales[axis]!.ticks!.color = ticksColorCallback;
    }
    if (Utils.isNotNullOrUndefined(gridColorCallback)) {
      chart.options.scales[axis]!.grid!.color = gridColorCallback;
    }

    chart.options.plugins!.zoom!.limits![axis] = {
      min: min,
      max: max,
      minRange: minRange,
    };
  }
}
